import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { LegalDocumentName } from '../api/types';
import { useApi } from '../auth/ApiProvider';
import { useSession } from '../auth/SessionProvider';
import { useI18n } from '../i18n/I18nProvider';
import { Button, Notice, Row, Stack } from './primitives';
import { AuthPreferences, BrandPanel, type BrandPanelProps } from './SignIn';
import { ErrorState, LoadingState } from './states';

/**
 * The privacy notice or the terms of use (Q67, decision 2026-10-14 #6), open to anyone: an invitee reads both
 * before they have an account. Rendered from the API's sections, so there is no markup to sanitise.
 */
export function LegalPage({ document, ...brand }: BrandPanelProps & { document: LegalDocumentName }) {
  const { t } = useI18n();
  const text = useLegalText(document);

  return (
    <div className="dg-auth dg-auth--wide">
      <div className="dg-auth__panel">
        <BrandPanel {...brand} />
        <article className="dg-card dg-legal">
          {text.isPending && <LoadingState />}
          {text.isError && <ErrorState error={text.error} onRetry={() => void text.refetch()} />}
          {text.data && (
            <Stack>
              <h1 style={{ fontSize: 'var(--dg-text-xl)' }}>{text.data.title}</h1>
              {text.data.draftNotice && <Notice tone="warning">{text.data.draftNotice}</Notice>}
              {text.data.sections.map((section) => (
                <section key={section.heading}>
                  <h2>{section.heading}</h2>
                  {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                  {section.items && <ul>{section.items.map((item) => <li key={item}>{item}</li>)}</ul>}
                </section>
              ))}
              <p className="dg-muted dg-text-sm">{t('legal.version', { version: text.data.version })}</p>
            </Stack>
          )}
        </article>
        <AuthPreferences />
      </div>
    </div>
  );
}

/** The two texts as links, opened in a new tab so a half-filled form behind them is not lost. */
export function LegalLinks() {
  const { t } = useI18n();
  return (
    <Row>
      <a href="/privacy" target="_blank" rel="noopener">{t('legal.read.privacy')}</a>
      <a href="/terms" target="_blank" rel="noopener">{t('legal.read.terms')}</a>
    </Row>
  );
}

/** The links under every screen shown before sign-in, and on the account screen. */
export function LegalFooter() {
  const { t } = useI18n();
  return (
    <nav className="dg-legal-footer" aria-label={t('legal.title')}>
      <Link to="/privacy">{t('legal.privacy')}</Link>
      <Link to="/terms">{t('legal.terms')}</Link>
    </nav>
  );
}

/** The box an invitee ticks to accept both texts, with links to read them. */
export function LegalConsent({ checked, onChange, error }: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  error?: string | null;
}) {
  const { t } = useI18n();
  return (
    <Stack tight>
      <LegalLinks />
      <label className="dg-check">
        <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} required />
        <span>{t('legal.consent')}</span>
      </label>
      {error && <p className="dg-field__error" role="alert">{error}</p>}
    </Stack>
  );
}

/**
 * Holds the signed-in app until the person has accepted the legal texts in force: a version published since they
 * joined, or, for the first platform administrator, every text (Q67). The server records the acceptance; this
 * gate is usability, and invitations are where acceptance is enforced.
 */
export function LegalGate({ children }: { children: ReactNode }) {
  const { session } = useSession();
  if (!session?.pendingLegal?.length) return <>{children}</>;
  return <LegalAcceptanceScreen />;
}

/** A legal text in the reader's language. It changes only with a release, so it is kept ten minutes. */
function useLegalText(document: LegalDocumentName) {
  const api = useApi();
  const { locale } = useI18n();
  return useQuery({
    queryKey: ['legal', document, locale],
    queryFn: () => api.legal.text(document, locale),
    staleTime: 10 * 60_000,
  });
}

function LegalAcceptanceScreen() {
  const api = useApi();
  const { t, locale } = useI18n();
  const { reload, signOut } = useSession();
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Both versions, read from the texts themselves, so what is accepted is what the links show.
  const privacy = useLegalText('privacy');
  const terms = useLegalText('terms');

  const accept = useMutation({
    mutationFn: () => api.legal.accept({
      privacyVersion: privacy.data!.version, termsVersion: terms.data!.version, language: locale,
    }),
    onSuccess: () => reload(),
  });

  function submit() {
    setError(checked ? null : t('legal.consent.required'));
    if (checked && privacy.data && terms.data) accept.mutate();
  }

  return (
    <div className="dg-auth">
      <div className="dg-auth__panel">
        <div className="dg-card">
          <Stack>
            <h1 style={{ fontSize: 'var(--dg-text-xl)' }}>{t('legal.updated.title')}</h1>
            <p>{t('legal.updated.body')}</p>
            {(privacy.isError || terms.isError) && <ErrorState error={privacy.error ?? terms.error} />}
            {accept.isError && <ErrorState error={accept.error} />}
            <LegalConsent checked={checked} onChange={setChecked} error={error} />
            <Button block size="large" busy={accept.isPending} disabled={!privacy.data || !terms.data} onClick={submit}>
              {t('legal.accept')}
            </Button>
            <Button variant="ghost" block onClick={() => void signOut()}>{t('auth.signOut')}</Button>
          </Stack>
        </div>
      </div>
    </div>
  );
}
